import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Phone, FileText, IndianRupee, Shield, ArrowRight, ExternalLink } from 'lucide-react';
import { formatINR } from '@/utils/currency';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessScreen({ assignment, unit, user, linkToken, navigate }) {
  const [docSent, setDocSent] = useState(false);

  const capitalAmount = (assignment.assignedAmount || 0) - 25000;
  const investorEmail = user?.email || assignment.investorEmail || '';
  const investorPhone = assignment.investorPhone || '';

  useEffect(() => {
    // Documents are automatically triggered by the backend after EOI conversion.
    // This is just a status check — no need to call a separate endpoint.
    setDocSent(true);
  }, []);

  const nextSteps = [
    { step: 1, title: 'Review & Sign Documents', desc: 'Check your email for the LLP partnership deed via Zoho Sign', status: 'current' },
    { step: 2, title: 'Capital Payment', desc: `After signing, pay the remaining ${formatINR(capitalAmount)}`, status: 'waiting' },
    { step: 3, title: 'Complete Investment', desc: 'Finalise your investment in the property', status: 'waiting' },
    { step: 4, title: 'Become LLP Partner', desc: `Own ${assignment.ownershipPercentage}% of ${unit.unitName || unit.unitNumber || 'the property'}`, status: 'waiting' },
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white" data-testid="payment-success-screen">
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        {/* Success Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <div className="w-16 h-16 bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#4ADE80]" />
          </div>
          <h2 className="text-2xl font-light tracking-tight mb-2">EOI Payment Received!</h2>
          <p className="text-white/50 text-lg font-mono" data-testid="eoi-confirmed-amount">{formatINR(25000)} confirmed</p>
        </motion.div>

        {/* Investment Summary */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-sm p-5 space-y-3">
          <h3 className="text-xs text-white/40 uppercase tracking-wider mb-3">Investment Summary</h3>
          <SummaryRow label="Property" value={unit.unitName || unit.unitNumber || 'Unit'} />
          {unit.location && <SummaryRow label="Location" value={typeof unit.location === 'object' ? unit.location.fullAddress || unit.location.city : unit.location} />}
          <SummaryRow label="Ownership" value={`${assignment.ownershipPercentage}%`} highlight />
          <SummaryRow label="Total Investment" value={formatINR(assignment.assignedAmount)} />
          <div className="h-px bg-white/5" />
          <div className="flex justify-between items-center py-2 px-3 bg-[#4ADE80]/5 border border-[#4ADE80]/10 rounded-sm">
            <span className="text-sm font-medium text-[#4ADE80]">EOI Paid</span>
            <span className="text-sm font-mono font-bold text-[#4ADE80]">{formatINR(25000)}</span>
          </div>
        </motion.div>

        {/* Document Notification */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-blue-500/5 border border-blue-400/20 rounded-sm p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 space-y-3">
              <h3 className="text-sm font-medium text-blue-300">Partnership Documents Sent</h3>
              <p className="text-xs text-white/50">Legal documents have been sent to your email for e-signature:</p>
              <div className="space-y-2">
                {investorEmail && (
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    <Mail className="w-3.5 h-3.5 text-white/30" />
                    <span className="font-mono">{investorEmail}</span>
                  </div>
                )}
                {investorPhone && (
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    <Phone className="w-3.5 h-3.5 text-white/30" />
                    <span className="font-mono">{investorPhone}</span>
                  </div>
                )}
              </div>
              {/* Action Required */}
              <div className="bg-yellow-400/5 border border-yellow-400/10 rounded-sm p-3">
                <p className="text-xs text-yellow-400/80">
                  <strong>Action Required:</strong> Please check your email and sign the partnership documents within 7 days.
                  After signing, you'll receive a link to complete the capital payment of {formatINR(capitalAmount)}.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* What Happens Next */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-sm p-5">
          <h3 className="text-xs text-white/40 uppercase tracking-wider mb-4">What Happens Next</h3>
          <div className="space-y-4">
            {nextSteps.map(item => (
              <div key={item.step} className="flex items-start gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono shrink-0 ${
                  item.status === 'current' ? 'bg-blue-500/20 border border-blue-400/40 text-blue-400' : 'bg-white/5 border border-white/10 text-white/30'
                }`}>
                  {item.step}
                </div>
                <div className="pt-0.5">
                  <p className={`text-sm font-medium ${item.status === 'current' ? 'text-white' : 'text-white/40'}`}>{item.title}</p>
                  <p className="text-xs text-white/30 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="flex gap-3">
          <Button onClick={() => navigate('/investor')}
            className="flex-1 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 py-5 font-medium" data-testid="go-dashboard-btn">
            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          {investorEmail && (
            <Button onClick={() => window.open(`https://mail.google.com`, '_blank')}
              variant="outline" className="flex-1 border-white/10 text-white/60 hover:bg-white/5 py-5" data-testid="open-email-btn">
              Open Email <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          )}
        </motion.div>

        {/* Support */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="text-center space-y-2 pt-4">
          <p className="text-xs text-white/20">Need help?</p>
          <div className="flex items-center justify-center gap-4">
            <a href="mailto:support@altslice.com" className="text-xs text-white/40 hover:text-white flex items-center gap-1"><Mail className="w-3 h-3" />support@altslice.com</a>
            <a href="tel:+919876543210" className="text-xs text-white/40 hover:text-white flex items-center gap-1"><Phone className="w-3 h-3" />+91 98765 43210</a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-xs text-white/40">{label}</span>
      <span className={`text-sm font-mono ${highlight ? 'text-[#4ADE80]' : 'text-white'}`}>{value}</span>
    </div>
  );
}
